import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import { financialService } from '../../../services/api';

const FinancialOverviewCards = ({ selectedPeriod, selectedHotel }) => {
  const [overviewData, setOverviewData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    loadFinancialSummary();
  }, [selectedPeriod, selectedHotel]);

  const loadFinancialSummary = async () => {
    try {
      setLoading(true);
      setError(null);

      const params = {};
      if (selectedHotel?.id) {
        params.hotelId = selectedHotel.id;
      }

      // Para os cards de overview, sempre buscar dados do período atual (usar UTC)
      const now = new Date();
      params.startDate = new Date(Date.UTC(now.getFullYear(), now.getMonth(), 1)).toISOString();
      params.endDate = new Date(Date.UTC(now.getFullYear(), now.getMonth() + 1, 0)).toISOString();

      console.log('📊 Buscando dados financeiros com params:', params);
      const summary = await financialService.getSummary(params);
      console.log('📊 Dados recebidos:', summary);
      
      // Calcular margem de lucro
      const profitMargin = summary.totalIncome > 0 
        ? ((summary.netProfit / summary.totalIncome) * 100).toFixed(1)
        : 0;

      setOverviewData({
        totalIncome: summary.totalIncome,
        totalExpense: summary.totalExpense,
        netProfit: summary.netProfit,
        cashFlow: summary.netProfit, // Simplificado - fluxo de caixa = lucro líquido
        profitMargin,
        reservationRevenue: summary.reservationRevenue,
        transactionIncome: summary.transactionIncome,
      });
    } catch (err) {
      console.error('Erro ao carregar resumo financeiro:', err);
      setError('Erro ao carregar dados financeiros');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value || 0);
  };

  if (loading) {
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
        {[...Array(4)].map((_, index) => (
          <div key={index} className="bg-card border border-border rounded-lg p-6 shadow-sm animate-pulse">
            <div className="h-4 bg-muted rounded w-3/4 mb-2"></div>
            <div className="h-8 bg-muted rounded w-1/2 mb-2"></div>
            <div className="h-3 bg-muted rounded w-1/3"></div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-card border border-border rounded-lg p-6 shadow-sm">
        <div className="flex items-center space-x-3 text-destructive">
          <Icon name="AlertCircle" size={20} />
          <p>{error}</p>
        </div>
      </div>
    );
  }

  if (!overviewData) {
    return null;
  }

  const cardsData = [
    {
      id: 1,
      title: "Receita Total",
      value: formatCurrency(overviewData.totalIncome),
      icon: "TrendingUp",
      description: `Reservas: ${formatCurrency(overviewData.reservationRevenue)} | Transações: ${formatCurrency(overviewData.transactionIncome)}`,
      color: "success"
    },
    {
      id: 2,
      title: "Despesas Totais",
      value: formatCurrency(overviewData.totalExpense),
      icon: "TrendingDown",
      description: "Despesas operacionais e administrativas",
      color: "warning"
    },
    {
      id: 3,
      title: "Lucro Líquido",
      value: formatCurrency(overviewData.netProfit),
      icon: "DollarSign",
      description: `Margem de lucro: ${overviewData.profitMargin}%`,
      color: "primary"
    },
    {
      id: 4,
      title: "Fluxo de Caixa",
      value: formatCurrency(overviewData.cashFlow),
      icon: "Wallet",
      description: "Saldo líquido disponível",
      color: "secondary"
    }
  ];

  const getColorClasses = (color) => {
    switch (color) {
      case 'success':
        return {
          bg: 'bg-success/10',
          text: 'text-success',
          border: 'border-success/20'
        };
      case 'warning':
        return {
          bg: 'bg-warning/10',
          text: 'text-warning',
          border: 'border-warning/20'
        };
      case 'primary':
        return {
          bg: 'bg-primary/10',
          text: 'text-primary',
          border: 'border-primary/20'
        };
      case 'secondary':
        return {
          bg: 'bg-secondary/10',
          text: 'text-secondary',
          border: 'border-secondary/20'
        };
      default:
        return {
          bg: 'bg-muted/10',
          text: 'text-muted-foreground',
          border: 'border-border'
        };
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
      {cardsData.map((card) => {
        const colors = getColorClasses(card.color);
        
        return (
          <div
            key={card.id}
            className={`group relative bg-gradient-to-br from-card to-card/50 border ${colors.border} rounded-xl p-5 shadow-sm hover:shadow-lg transition-all duration-300 hover:-translate-y-1 overflow-hidden`}
          >
            {/* Background decoration */}
            <div className={`absolute top-0 right-0 w-32 h-32 ${colors.bg} rounded-full blur-3xl opacity-20 -mr-16 -mt-16 group-hover:opacity-30 transition-opacity`}></div>
            
            <div className="relative">
              <div className="flex items-start justify-between mb-3">
                <div className={`p-2.5 rounded-xl ${colors.bg} group-hover:scale-110 transition-transform duration-300`}>
                  <Icon name={card.icon} size={22} className={colors.text} />
                </div>
                
                {/* Trend indicator - você pode adicionar lógica real aqui */}
                <div className="flex items-center space-x-1 text-xs">
                  <svg className={`w-3.5 h-3.5 ${card.id === 2 ? 'text-destructive rotate-180' : 'text-success'}`} fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5.293 7.707a1 1 0 010-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 01-1.414 1.414L11 5.414V17a1 1 0 11-2 0V5.414L6.707 7.707a1 1 0 01-1.414 0z" clipRule="evenodd" />
                  </svg>
                  <span className={card.id === 2 ? 'text-destructive font-medium' : 'text-success font-medium'}>
                    {card.id === 2 ? '-8%' : '+12%'}
                  </span>
                </div>
              </div>
              
              <div className="space-y-2">
                <h3 className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                  {card.title}
                </h3>
                <p className={`text-2xl font-bold ${colors.text} transition-colors`}>
                  {card.value}
                </p>
                <p className="text-xs text-muted-foreground leading-relaxed pt-1 border-t border-border/50">
                  {card.description}
                </p>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export default FinancialOverviewCards;